import React, { useMemo, useState } from 'react';
import {
  FileText,
  Download,
  FileSpreadsheet,
  FileJson,
  Eye,
  Trash2,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  exportCSV,
  exportJSON,
  exportSinglePDF,
  exportAllPDF,
} from '../../lib/exporters';
import { deleteWaiver as removeWaiver } from '../../lib/waiverStorage';

export default function WaiversTab({ waivers, onChange, onView }) {
  const [query, setQuery] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return waivers;
    return waivers.filter((w) =>
      [w.name, w.email, w.phone, w.date, w.id].some((f) =>
        String(f || '').toLowerCase().includes(q)
      )
    );
  }, [waivers, query]);

  const handleDelete = (id) => {
    removeWaiver(id);
    setConfirmDelete(null);
    onChange?.();
    toast.success('Waiver deleted.');
  };

  return (
    <div>
      <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between mb-6">
        <div className="relative w-full lg:max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, phone, date..."
            className="w-full h-14 pl-12 pr-4 rounded-2xl border border-white/10 bg-white text-slate-100 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 focus:outline-none"
            data-testid="admin-search-input"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => {
              if (!waivers.length) return toast.error('No waivers to export.');
              exportCSV(filtered);
              toast.success('CSV exported.');
            }}
            className="pill-btn inline-flex items-center gap-2 h-12 px-5 rounded-full bg-white border border-white/10 hover:border-blue-400 hover:text-blue-300 text-slate-200 font-semibold shadow-sm"
            data-testid="admin-export-csv-button"
          >
            <FileSpreadsheet className="w-4 h-4" />
            CSV
          </button>
          <button
            onClick={() => {
              if (!waivers.length) return toast.error('No waivers to export.');
              exportJSON(filtered);
              toast.success('JSON exported.');
            }}
            className="pill-btn inline-flex items-center gap-2 h-12 px-5 rounded-full bg-white border border-white/10 hover:border-blue-400 hover:text-blue-300 text-slate-200 font-semibold shadow-sm"
            data-testid="admin-export-json-button"
          >
            <FileJson className="w-4 h-4" />
            JSON
          </button>
          <button
            onClick={() => {
              if (!waivers.length) return toast.error('No waivers to export.');
              exportAllPDF(filtered);
              toast.success('PDF export started.');
            }}
            className="pill-btn inline-flex items-center gap-2 h-12 px-5 rounded-full bg-blue-500 hover:bg-blue-500 text-white font-semibold shadow-md shadow-blue-500/30"
            data-testid="admin-export-pdf-button"
          >
            <Download className="w-4 h-4" />
            PDF (All)
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-950 border-b border-white/10">
              <tr className="text-xs tracking-[0.15em] uppercase text-slate-300">
                <th className="px-6 py-4 font-semibold">Name</th>
                <th className="px-6 py-4 font-semibold hidden md:table-cell">Email</th>
                <th className="px-6 py-4 font-semibold hidden lg:table-cell">Phone</th>
                <th className="px-6 py-4 font-semibold">Date</th>
                <th className="px-6 py-4 font-semibold hidden md:table-cell">Signed</th>
                <th className="px-6 py-4 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="admin-waivers-table-body">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center text-slate-400">
                    {waivers.length === 0
                      ? 'No signed waivers yet. When a client signs, it will appear here.'
                      : 'No waivers match your search.'}
                  </td>
                </tr>
              )}
              {filtered.map((w) => (
                <tr
                  key={w.id}
                  className="border-b border-white/10 hover:bg-blue-500/10/30 transition-colors"
                  data-testid={`admin-waiver-row-${w.id}`}
                >
                  <td className="px-6 py-4 text-white font-semibold">{w.name}</td>
                  <td className="px-6 py-4 text-slate-200 hidden md:table-cell">{w.email}</td>
                  <td className="px-6 py-4 text-slate-200 hidden lg:table-cell">{w.phone}</td>
                  <td className="px-6 py-4 text-slate-200">{w.date}</td>
                  <td className="px-6 py-4 text-slate-400 text-sm hidden md:table-cell">
                    {new Date(w.signedAt).toLocaleString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => onView?.(w)}
                        className="pill-btn inline-flex items-center gap-1.5 h-10 px-3.5 rounded-full bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 text-sm font-semibold border border-blue-500/20"
                        data-testid={`admin-view-button-${w.id}`}
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </button>
                      <button
                        onClick={() => {
                          exportSinglePDF(w);
                          toast.success('PDF exported.');
                        }}
                        className="pill-btn inline-flex items-center gap-1.5 h-10 px-3.5 rounded-full bg-white hover:bg-slate-950 text-slate-200 text-sm font-semibold border border-white/10"
                        data-testid={`admin-download-pdf-${w.id}`}
                      >
                        <FileText className="w-4 h-4" />
                        PDF
                      </button>
                      <button
                        onClick={() => setConfirmDelete(w)}
                        className="pill-btn inline-flex items-center justify-center h-10 w-10 rounded-full bg-white hover:bg-red-50 hover:text-red-600 text-slate-400 border border-white/10"
                        data-testid={`admin-delete-button-${w.id}`}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {confirmDelete && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setConfirmDelete(null)}
          data-testid="admin-delete-modal"
        >
          <div
            className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-8 py-6">
              <h3 className="font-display text-2xl text-slate-100">Delete waiver?</h3>
              <p className="mt-2 text-slate-300">
                This will permanently remove <span className="font-semibold text-slate-100">{confirmDelete.name}</span>&rsquo;s signed waiver from this device. This cannot be undone.
              </p>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="pill-btn h-11 px-5 rounded-full bg-slate-900 hover:bg-slate-800 text-slate-200 font-semibold text-sm"
                  data-testid="admin-delete-cancel-button"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(confirmDelete.id)}
                  className="pill-btn h-11 px-5 rounded-full bg-red-500 hover:bg-red-600 text-white font-semibold text-sm"
                  data-testid="admin-delete-confirm-button"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
